import { inject, Injectable } from '@angular/core';
import { AuthService } from '@hyper-inferno/hyper-base-ng';
import {
  AbstractSurveyService,
  SurveyBotEngine,
  SurveyBundle,
  SurveySession,
  SurveySessionStatus,
  SyncStatus,
  UniversalPiping,
} from '@hyper-inferno/survey-bot-core';
import {
  ComposedElementBuilder,
  ComposedPage,
  ComposedPageBuilder,
  HandshakeTokenPayload,
} from '@hyper-inferno/survey-elements-core-utils';
import { RxDBProviderService } from './rx-db.service';

import { OpenEndedHandler } from '@hyper-inferno/open-ended-handler';
import { SingleChoiceHandler } from '@hyper-inferno/single-choice-handler';
import { ClickToRankHandler } from '@hyper-inferno/click-to-rank-handler';
import { DropDownMultipleChoiceHandler } from '@hyper-inferno/drop-down-multiple-choice-handler';
import { DropDownSingleChoiceHandler } from '@hyper-inferno/drop-down-single-choice-handler';
import { ImageHandler } from '@hyper-inferno/image-handler';
import { MultipleChoiceHandler } from '@hyper-inferno/multiple-choice-handler';
import { NumericHandler } from '@hyper-inferno/numeric-handler';
import { SkipLogicHandler } from '@hyper-inferno/skip-logic-handler';
import { VideoHandler } from '@hyper-inferno/video-handler';
import { VoiceHandler } from '@hyper-inferno/voice-handler';
import { GridSingleChoiceHandler } from '@hyper-inferno/grid-single-choice-handler';
import { GridMultipleChoiceHandler } from '@hyper-inferno/grid-multiple-choice-handler';
import { DisplayHandler } from '@hyper-inferno/display-handler';
import { LocationHandler } from '@hyper-inferno/location-handler';
import { GridMixedHandler } from '@hyper-inferno/grid-mixed-handler';

import {
  ElementType,
  PageType,
  QuestionType,
} from '@hyper-inferno/survey-elements-core';
import { SurveyLogin } from '@hyper-inferno/survey-elements-core-ui';
export interface LoginPageSettings {
  id: string;
  languages: LanguageOption[];
  error_message: string;
  required?: boolean;
  survey_id: number;
}

export interface LanguageOption {
  code: string; // e.g., 'en', 'es'
  label: string; // e.g., 'English', 'Español'
}
@Injectable({
  providedIn: 'root',
})
export class SurveyBotController extends AbstractSurveyService {
  protected handlers = {
    [QuestionType.OPEN_ENDED]: new OpenEndedHandler(),
    [QuestionType.SINGLE_CHOICE]: new SingleChoiceHandler(),
    [QuestionType.MULTIPLE_CHOICE]: new MultipleChoiceHandler(),
    [QuestionType.DROP_DOWN_SINGLE_CHOICE]: new DropDownSingleChoiceHandler(),
    [QuestionType.DROP_DOWN_MULTIPLE_CHOICE]:
      new DropDownMultipleChoiceHandler(),
    [QuestionType.CLICK_TO_RANK]: new ClickToRankHandler(),
    [QuestionType.VOICE]: new VoiceHandler(),
    [QuestionType.VIDEO]: new VideoHandler(),
    [QuestionType.IMAGE]: new ImageHandler(),
    [QuestionType.NUMERIC]: new NumericHandler(),
    [QuestionType.SKIP_LOGIC]: new SkipLogicHandler(),
    [QuestionType.SINGLE_GRID]: new GridSingleChoiceHandler(),
    [QuestionType.MULTIPLE_GRID]: new GridMultipleChoiceHandler(),
    [QuestionType.DISPLAY]: new DisplayHandler(),
    [QuestionType.LOCATION]: new LocationHandler(),
    [QuestionType.MIXED_GRID]: new GridMixedHandler(),
  };

  private rxdb = inject(RxDBProviderService);

  protected override getHandshakeTokenString(
    handshakeTokenPayload: HandshakeTokenPayload,
  ): string {
    return JSON.stringify(handshakeTokenPayload);
  }

  protected override async getEngine(
    session_id: string,
    params?: any,
  ): Promise<SurveyBotEngine> {
    const db = await this.rxdb.init();
    const sessionDoc = await db.survey_sessions.findOne(session_id).exec();
    if (!sessionDoc) {
      throw new Error('No Session');
    }

    // 1. Get the downloaded bundle
    const bundleDoc = await db.survey_versions
      .findOne({
        selector: { survey_id: sessionDoc.survey_id },
        sort: [{ version_number: 'desc' }],
      })
      .exec();

    if (!bundleDoc) throw new Error('Survey Bundle not found offline');

    // 2. Wrap the RxDB document in the SurveySession class
    // We merge the new answers (params) just like your NestJS RPC does
    const sessionData = sessionDoc.toJSON();
    const upsertedSession: any = {
      ...sessionData,
      jsn_responses: {
        ...sessionData.jsn_responses,
        ...params,
      },
    };
    const session = new SurveySession(upsertedSession);

    const bundle = new SurveyBundle(bundleDoc);

    // 3. Initialize the Engine with local config
    return new SurveyBotEngine({
      bundle,
      session,
      listBuilding: {},
      piping: {
        transform: (text, ctx) => new UniversalPiping().transform(text, ctx), // Standard piping implementation
      },
      sessionSave: async (sessionData) => {
        // IMPORTANT: Save the engine state back to RxDB
        await sessionDoc.incrementalPatch({
          ...sessionData,
          sync_status: 'dirty', // 👈 This "pings" the watcher
        });
        return true;
      },
      handlers: this.handlers,
    });
  }

  protected override async createSession(arg0: {
    id: string;
    survey_id: number;
    sys_survey_start_time: string;
    jsn_responses: {};
    sys_last_updated: string;
    session_status: SurveySessionStatus;
    updated_at: string;
    sync_status: SyncStatus;
    sys_profile_identifier: string;
    session_unique_identifier: string;
  }): Promise<void> {
    const db = await this.rxdb.init();

    const doc = await db.survey_sessions.findOne(arg0.id).exec();
    if (!doc) {
      // Only insert if it doesn't already exist
      await db.survey_sessions.insert(arg0);
    }
  }

  protected override async getVariablesFromVersion(survey_id: number) {
    const db = await this.rxdb.init();
    const bundle = await db.survey_versions
      .findOne({
        selector: { survey_id: survey_id },
        sort: [{ version_number: 'desc' }],
      })
      .exec();

    if (!bundle) throw new Error('Survey Bundle not found offline');
    return bundle._data.jsn_definition.variables ?? [];
  }

  protected override async verifyIfPasswordHasSession(
    survey_id: number,
    passKey: string,
    passValue: string,
  ): Promise<any | null> {
    try {
      // 1. Initialize and get your RxDatabase instance
      const db = await this.rxdb.init();

      // 2. Query the 'survey_session' collection using findOne
      const sessionDoc = await db.survey_sessions
        .findOne({
          selector: {
            survey_id: { $eq: survey_id },
            session_unique_identifier: { $eq: passValue }, // Using your new flat column
          },
        })
        .exec(); // Resolves the RxDB query into a promise

      // 3. Handle the result
      if (sessionDoc) {
        console.log('Access Granted: Matching session found.');

        // Return the matching structure expected by your application
        return sessionDoc;
      } else {
        console.log('Access Denied: No matching parameter found.');
        return null;
      }
    } catch (err: any) {
      console.error('Verification failed:', err);
      return null;
    }
  }
  protected override async handleNoPassword(passKey: string) {
    const password = window.prompt('Password not provided');
    if (password) return password;
    throw new Error(`${passKey} not available `);
  }
}
